import apiClient from './api';

/**
 * 批量任务API服务
 */
class BatchTaskService {
  
  /**
   * 创建成员活码批量任务
   * @param {Object} request - 批量创建请求
   * @returns {Promise} API响应
   */
  async createMemberContactWaysBatch(request) {
    try {
      const response = await apiClient.post('/batch-tasks/member-contact-ways', request);
      return response.data;
    } catch (error) {
      console.error('创建成员活码批量任务失败:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 追加成员活码到现有LBS
   */
  async appendMemberContactWaysToLbs(request) {
    try {
      const response = await apiClient.post('/batch-tasks/member-contact-ways/append-to-lbs', request);
      return response.data;
    } catch (error) {
      console.error('追加成员活码失败:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 创建群活码批量任务
   * @param {Object} request - 批量创建请求
   * @returns {Promise} API响应
   */
  async createGroupJoinWaysBatch(request) {
    try {
      const response = await apiClient.post('/batch-tasks/group-join-ways', request);
      return response.data;
    } catch (error) {
      console.error('创建群活码批量任务失败:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 追加群活码到现有LBS
   */
  async appendGroupJoinWaysToLbs(request) {
    try {
      const response = await apiClient.post('/batch-tasks/group-join-ways/append-to-lbs', request);
      return response.data;
    } catch (error) {
      console.error('追加群活码失败:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 获取批量任务详情
   * @param {string|number} taskId - 任务ID
   * @returns {Promise} 任务详情
   */
  async getBatchTaskDetail(taskId) {
    try {
      const response = await apiClient.get(`/batch-tasks/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('获取批量任务详情失败:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 获取批量任务进度
   * @param {string|number} taskId - 任务ID
   * @returns {Promise} 任务进度
   */
  async getBatchTaskProgress(taskId) {
    try {
      const response = await apiClient.get(`/batch-tasks/${taskId}/progress`);
      return response.data;
    } catch (error) {
      console.error('获取批量任务进度失败:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 获取批量任务列表
   * @param {Object} params - 查询参数
   * @param {number} params.page - 页码
   * @param {number} params.size - 每页大小
   * @param {string} params.taskType - 任务类型
   * @returns {Promise} 任务列表
   */
  async getBatchTasks(params = {}) {
    try {
      const {
        page = 0,
        size = 20,
        taskType
      } = params;

      const queryParams = { page, size };
      if (taskType) {
        queryParams.taskType = taskType;
      }

      const response = await apiClient.get('/batch-tasks', { params: queryParams });
      return response.data;
    } catch (error) {
      console.error('获取批量任务列表失败:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 重试失败的任务项
   * @param {string|number} taskId - 任务ID
   * @returns {Promise} API响应
   */
  async retryFailedItems(taskId) {
    try {
      const response = await apiClient.post(`/batch-tasks/${taskId}/retry-failed`);
      return response.data;
    } catch (error) {
      console.error('重试失败任务项失败:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 取消任务
   * @param {string|number} taskId - 任务ID
   * @returns {Promise} API响应
   */
  async cancelTask(taskId) {
    try {
      const response = await apiClient.post(`/batch-tasks/${taskId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('取消任务失败:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 删除批量任务
   * @param {string|number} taskId - 任务ID
   * @returns {Promise} API响应
   */
  async deleteBatchTask(taskId) {
    try {
      const response = await apiClient.delete(`/batch-tasks/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('删除批量任务失败:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 获取任务统计信息
   * @returns {Promise} 统计信息
   */
  async getTaskStatistics() {
    try {
      const response = await apiClient.get('/batch-tasks/statistics');
      return response.data;
    } catch (error) {
      console.error('获取任务统计信息失败:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 处理API错误
   * @param {Error} error - 错误对象
   * @returns {Error} 处理后的错误
   */
  handleError(error) {
    if (error.response) {
      // 服务器响应错误
      const { status, data } = error.response;
      const message = data?.message || `请求失败 (${status})`;
      
      return new Error(message);
    } else if (error.request) {
      // 网络错误
      return new Error('网络连接失败，请检查网络状态');
    } else {
      // 其他错误
      return new Error(error.message || '未知错误');
    }
  }

  /**
   * 格式化任务状态文本
   * @param {string} status - 任务状态
   * @returns {string} 格式化后的状态文本
   */
  formatTaskStatus(status) {
    const statusMap = {
      'PENDING': '等待执行',
      'RUNNING': '执行中',
      'COMPLETED': '已完成',
      'FAILED': '执行失败'
    };
    return statusMap[status] || status;
  }

  /**
   * 格式化任务类型文本
   * @param {string} taskType - 任务类型
   * @returns {string} 格式化后的类型文本
   */
  formatTaskType(taskType) {
    const typeMap = {
      'MEMBER_CONTACT_WAY_BATCH': '成员活码批量创建',
      'GROUP_JOIN_WAY_BATCH': '群活码批量创建'
    };
    return typeMap[taskType] || taskType;
  }

  /**
   * 获取任务状态颜色
   * @param {string} status - 任务状态
   * @returns {string} Ant Design的状态颜色
   */
  getTaskStatusColor(status) {
    const colorMap = {
      'PENDING': 'default',
      'RUNNING': 'processing',
      'COMPLETED': 'success',
      'FAILED': 'error'
    };
    return colorMap[status] || 'default';
  }

  /**
   * 计算任务执行时长
   * @param {string} startedAt - 开始时间
   * @param {string} completedAt - 完成时间
   * @returns {string} 格式化的执行时长
   */
  formatTaskDuration(startedAt, completedAt) {
    if (!startedAt) {
      return '未开始';
    }

    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const duration = end - start;

    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}小时${minutes % 60}分钟`;
    } else if (minutes > 0) {
      return `${minutes}分钟${seconds % 60}秒`;
    } else {
      return `${seconds}秒`;
    }
  }

  /**
   * 估算剩余时间
   * @param {Object} progress - 进度信息
   * @returns {string} 估算的剩余时间
   */
  estimateRemainingTime(progress) {
    const { startedAt, completedCount, totalCount } = progress;
    
    if (!startedAt || completedCount === 0) {
      return '计算中...';
    }

    const elapsed = Date.now() - new Date(startedAt).getTime();
    const avgTimePerItem = elapsed / completedCount;
    const remainingItems = totalCount - completedCount;
    const estimatedRemaining = avgTimePerItem * remainingItems;

    const remainingSeconds = Math.floor(estimatedRemaining / 1000);
    const remainingMinutes = Math.floor(remainingSeconds / 60);
    const remainingHours = Math.floor(remainingMinutes / 60);

    if (remainingHours > 0) {
      return `约${remainingHours}小时${remainingMinutes % 60}分钟`;
    } else if (remainingMinutes > 0) {
      return `约${remainingMinutes}分钟`;
    } else {
      return `约${remainingSeconds}秒`;
    }
  }
}

// 创建服务实例
const batchTaskService = new BatchTaskService();

export default batchTaskService;